import React, { useState } from 'react';
import { TimeDropdown, YesNoDropdown, FLOAT_REGEX, MAXIMUM_RATE, DateTimeDropdown } from './AddShiftsPage.jsx';
import { MILLISECONDS_IN_AN_HOUR } from '../../Constants.js';
import ConfirmationModal from './ConfirmationModal.jsx';
import { getShiftsForStore, postShiftEdit } from '../../Api.js';
import { formatDatesWithTimeZone } from '../../../helpers/DateUtils.js';
import { ReasonDropdown } from '../common/ReasonDropdown.jsx';
import useStore from '../../hooks/StoreHook.jsx';
import { getTimezoneOffset } from 'date-fns-tz';

const EDIT_OPTIONS = {
  MISTAKE: {
    key: 'MISTAKE',
    text: 'Mistake with shift details',
  },
  NEGOTIATE: {
    key: 'NEGOTIATE',
    text: 'Pre-negotiated with locum',
  },
  CHANGE_OF_DATE: {
    key: 'CHANGE_OF_DATE',
    text: 'Date needs adjustment',
  },
  INCREASE_SPEED: {
    key: 'INCREASE_SPEED',
    text: 'Want to fill the shift faster',
  },
  OTHER: {
    key: 'OTHER',
    text: 'Other',
  }
};

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

export const getTimeOffset = (time, timeZone) => {
  const offsetDate = new Date(time);
  const timeZoneOffset = getTimezoneOffset(timeZone, offsetDate);
  const offsetTimeInUTC = time % DAY_IN_MILLISECONDS;
  const offsetTime = offsetTimeInUTC + timeZoneOffset;
  if (offsetTime < 0) {
    return DAY_IN_MILLISECONDS + offsetTime;
  }
  return offsetTime % DAY_IN_MILLISECONDS;
};

const BULK_EDIT_FIELD_COPY = {
  startTime: {
    title: 'Start time',
    description: 'Set the same start time for every selected shift. Dates stay unchanged.',
  },
  endTime: {
    title: 'End time',
    description: 'Set the same end time for every selected shift. Dates stay unchanged.',
  },
  unpaidBreakTime: {
    title: 'Unpaid break',
    description: 'Set the same unpaid break for every selected shift.',
  },
  rate: {
    title: 'Rate',
    description: 'Set the same hourly rate for every selected shift.',
  },
  negotiable: {
    title: 'Negotiable',
    description: 'Set the same negotiable setting for every selected shift.',
  },
  billToMedicare: {
    title: 'Bill to Medicare',
    description: 'Set whether every selected shift is billed to Medicare.',
  },
};

const getBulkEditSummary = (selectedFields, bulkShiftCount) => {
  const changing = Object.keys(BULK_EDIT_FIELD_COPY).filter(field => selectedFields[field]);
  if (changing.length === 0) {
    return `Select at least one field to update for ${bulkShiftCount} selected shifts.`;
  }
  const changingText = changing.map(field => BULK_EDIT_FIELD_COPY[field].title).join(', ');
  return `This will update ${changingText} for ${bulkShiftCount} selected shift${bulkShiftCount === 1 ? '' : 's'}. All other fields will remain unchanged.`;
};

const BulkEditField = ({ field, selectedFields, toggleBulkField, loading, disabled = false, disabledText = 'This field is not available.', children }) => {
  const selected = selectedFields[field];
  const copy = BULK_EDIT_FIELD_COPY[field];
  const unavailable = disabled && !selected;
  return (
    <section className={`bulk-edit-section ${selected ? 'selected' : ''} ${unavailable ? 'disabled' : ''}`}>
      <section className={'bulk-edit-section-header'}>
        <section className={'bulk-edit-section-copy'}>
          <span className={'bulk-edit-section-title'}>{copy.title}</span>
          <span className={'bulk-edit-section-description'}>{copy.description}</span>
        </section>
        <label className={'bulk-edit-toggle'}>
          <input type={'checkbox'} checked={selected} onChange={() => toggleBulkField(field)} disabled={loading || disabled} />
          <span className={'bulk-edit-toggle-text'}>{selected ? 'Changing' : 'Not changing'}</span>
        </label>
      </section>
      {selected
        ? <section className={'bulk-edit-section-fields'}>{children}</section>
        : <span className={'bulk-edit-unchanged-text'}>{unavailable ? disabledText : 'This field will not change.'}</span>
      }
    </section>
  );
};

const EditShiftConfirmation = ({ close, confirm, setReason, reason, shift, errorText, bulkShiftCount = null, selectedFields = null, setSelectedFields = null }) => {
  const { address, startTime, endTime } = shift;
  const { timeZone } = address;
  const { date } = formatDatesWithTimeZone(startTime, endTime, false, timeZone);
  const initialStartTimeOffset = getTimeOffset(startTime, timeZone);
  const initialEndTimeOffset = getTimeOffset(endTime, timeZone);
  const shiftTimeOffset = startTime - initialStartTimeOffset;
  const [startTimeOffset, setStartTimeOffset] = useState(initialStartTimeOffset);
  const [endTimeOffset, setEndTimeOffset] = useState(initialEndTimeOffset);
  const [billToMedicare, setBillToMedicare] = useState(shift.billToMedicare ? shift.billToMedicare : false);
  const [breakTime, setBreakTime] = useState(shift.unpaidBreakTime * 60 / MILLISECONDS_IN_AN_HOUR);
  const [rate, setRate] = useState((shift.rate / 100).toFixed(2));
  const [negotiable, setNegotiable] = useState(shift.negotiable);
  const [loading, setLoading] = useState(false);
  const bulkEdit = bulkShiftCount !== null;
  const noBulkFieldsSelected = bulkEdit && !Object.values(selectedFields).some(Boolean);
  const disableMedicarePayFields = bulkEdit && selectedFields.billToMedicare && billToMedicare;
  const isBulkFieldSelected = (field) => !bulkEdit || selectedFields[field];
  const toggleBulkField = (field) => {
    if (disableMedicarePayFields && (field === 'rate' || field === 'negotiable')) {
      return;
    }
    setSelectedFields((currentFields) => {
      const nextFields = { ...currentFields, [field]: !currentFields[field] };
      if (field === 'billToMedicare' && nextFields.billToMedicare && billToMedicare) {
        nextFields.rate = false;
        nextFields.negotiable = false;
      }
      return nextFields;
    });
  };
  const setBulkBillToMedicare = (selected) => {
    setBillToMedicare(selected);
    if (selected) {
      setSelectedFields((currentFields) => ({ ...currentFields, rate: false, negotiable: false }));
    }
  };
  const onChangeRate = (e) => {
    if (e.target.value === '') {
      setRate(0);
    }
    else if (FLOAT_REGEX.test(e.target.value) && parseFloat(e.target.value) <= MAXIMUM_RATE) {
      setRate(e.target.value);
    }
  };
  const selectStartTime = (timeOffset) => {
    const duration = Math.round((endTimeOffset - timeOffset) / MILLISECONDS_IN_AN_HOUR);
    if (duration * 60 < breakTime) {
      setBreakTime(duration * 60);
    }
    setStartTimeOffset(timeOffset);
  };
  const selectEndTime = (timeOffset) => {
    const duration = Math.round((timeOffset - startTimeOffset) / MILLISECONDS_IN_AN_HOUR);
    if (duration * 60 < breakTime) {
      setBreakTime(duration * 60);
    }
    setEndTimeOffset(timeOffset);
  };
  const onClose = () => {
    if (loading) {
      return;
    }
    setStartTimeOffset(initialStartTimeOffset);
    setEndTimeOffset(initialEndTimeOffset);
    setBreakTime(Math.round(shift.unpaidBreakTime * 60 / MILLISECONDS_IN_AN_HOUR));
    setRate((shift.rate / 100).toFixed(2));
    setNegotiable(shift.negotiable);
    setBillToMedicare(shift.billToMedicare);
    close();
  };
  const duration = (endTimeOffset - startTimeOffset) / MILLISECONDS_IN_AN_HOUR;
  const total = ((duration - breakTime / 60) * rate).toFixed(2);
  const bulkSummaryText = bulkEdit ? getBulkEditSummary(selectedFields, bulkShiftCount) : null;
  const onConfirm = () => {
    if (bulkEdit && noBulkFieldsSelected) {
      return;
    }
    if (bulkEdit) {
      confirm(setLoading, startTimeOffset, endTimeOffset, breakTime, rate, negotiable, billToMedicare);
      return;
    }
    const newStartTime = shiftTimeOffset + startTimeOffset;
    const newEndTime = shiftTimeOffset + endTimeOffset;
    confirm(setLoading, newStartTime, newEndTime, breakTime, rate, negotiable, total, billToMedicare);
  };
  return (
    <ConfirmationModal
      close={onClose}
      confirm={onConfirm}
      confirmText={bulkEdit ? 'Edit selected shifts' : 'Edit this shift'}
      disabled={reason === null || noBulkFieldsSelected}
      additionalClassName={'edit'}
      loading={loading}
      loadingText={bulkEdit ? `Editing ${bulkShiftCount} selected shifts. This may take a moment.` : null}
    >
      <h3 className={'confirmation-title'}>{'Are you sure?'}</h3>
        <p className={'confirmation-text'}>
          {bulkEdit
            ? `You are editing ${bulkShiftCount} selected shifts. Dates will stay unchanged, and applicants or assigned Locums will be notified where applicable.`
            : `If you have already received applicants for this shift, we will notify them of the changes.`
          }
        </p>
        {bulkEdit
          ? <section className={'bulk-edit-sections'}>
              <span className={'bulk-edit-heading'}>{'Choose what to update for all selected shifts'}</span>
              <BulkEditField field={'startTime'} selectedFields={selectedFields} toggleBulkField={toggleBulkField} loading={loading}>
                <DateTimeDropdown selected={startTimeOffset} setSelected={selectStartTime} endTimeOffset={endTimeOffset} />
              </BulkEditField>
              <BulkEditField field={'endTime'} selectedFields={selectedFields} toggleBulkField={toggleBulkField} loading={loading}>
                <DateTimeDropdown selected={endTimeOffset} setSelected={selectEndTime} startTimeOffset={startTimeOffset} />
              </BulkEditField>
              <BulkEditField field={'unpaidBreakTime'} selectedFields={selectedFields} toggleBulkField={toggleBulkField} loading={loading}>
                <TimeDropdown selected={breakTime} setSelected={setBreakTime} intervalSize={15} maxSize={Math.min(duration * 60, 120)} />
              </BulkEditField>
              <BulkEditField field={'rate'} selectedFields={selectedFields} toggleBulkField={toggleBulkField} loading={loading} disabled={disableMedicarePayFields} disabledText={'Rate cannot be changed when Bill to Medicare is Yes.'}>
                <input className={'rate'} type={'text'} placeholder={'0.00'} value={rate} onChange={onChangeRate} />
              </BulkEditField>
              <BulkEditField field={'negotiable'} selectedFields={selectedFields} toggleBulkField={toggleBulkField} loading={loading} disabled={disableMedicarePayFields} disabledText={'Negotiable cannot be changed when Bill to Medicare is Yes.'}>
                <YesNoDropdown selected={negotiable} setSelected={setNegotiable} />
              </BulkEditField>
              <BulkEditField field={'billToMedicare'} selectedFields={selectedFields} toggleBulkField={toggleBulkField} loading={loading}>
                <YesNoDropdown selected={billToMedicare} setSelected={setBulkBillToMedicare} />
              </BulkEditField>
              <section className={`bulk-edit-summary ${noBulkFieldsSelected ? 'warning' : ''}`}>{bulkSummaryText}</section>
            </section>
          : <ul className={'shift-changes'}>
              <li className={'change-item'}>
                <span className={'change-label'}>{'Date: '}</span>
                <p className={'change-item'}>{date}</p>
              </li>
              <li className={'change-item'}>
                <span className={'change-label'}>{'Start Time: '}</span>
                <DateTimeDropdown selected={startTimeOffset} setSelected={selectStartTime} endTimeOffset={endTimeOffset} disabled={!isBulkFieldSelected('startTime')} />
              </li>
              <li className={'change-item'}>
                <span className={'change-label'}>{'End Time: '}</span>
                <DateTimeDropdown selected={endTimeOffset} setSelected={selectEndTime} startTimeOffset={startTimeOffset} disabled={!isBulkFieldSelected('endTime')} />
              </li>
              <li className={'change-item'}>
                <span className={'change-label'}>{'Unpaid Break: '}</span>
                <TimeDropdown selected={breakTime} setSelected={setBreakTime} intervalSize={15} maxSize={Math.min(duration * 60, 120)} disabled={!isBulkFieldSelected('unpaidBreakTime')} />
              </li>
              <li className={'change-item'}>
                <span className={'change-label'}>{'Bill to Medicare: '}</span>
                <YesNoDropdown selected={billToMedicare} setSelected={setBillToMedicare} disabled={!isBulkFieldSelected('billToMedicare')} />
              </li>
              <li className={'change-item'}>
                <span className={'change-label'}>{'Rate per hour: '}</span>
                <input className={`rate ${billToMedicare || !isBulkFieldSelected('rate') ? 'disabled' : ''}`} type={'text'} placeholder={'0.00'} value={billToMedicare ? 'NA' : rate} onChange={onChangeRate} disabled={billToMedicare || !isBulkFieldSelected('rate')}/>
              </li>
              <li className={'change-item'}>
                <span className={'change-label'}>{'Negotiable: '}</span>
                <YesNoDropdown selected={negotiable} setSelected={setNegotiable} disabled={billToMedicare || !isBulkFieldSelected('negotiable')} />
              </li>
              <li className={'change-item'}>
                <span className={'change-label'}>{'Total: '}</span>
                <span className={'bold'}>{`$${total}`}</span>
              </li>
            </ul>
        }
        <section className={'reason-dropdown-container'}>
          <span className={'reason-text'}>{'Reason: '}</span>
          <ReasonDropdown setReason={setReason} reason={reason} options={EDIT_OPTIONS} />
        </section>
        <section className={'error-text'}>{errorText}</section>
    </ConfirmationModal>
  );
};

export const EditShiftButton = ({ shift, editText, select = () => {}, classNames, store, disabled = false }) => {
  const { setShifts } = useStore();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(null);
  const close = () => {
    setOpen(false);
    setReason(null);
    setErrorText(null);
  };
  const [errorText, setErrorText] = useState(null);
  const confirm = (setLoading, startTime, endTime, unpaidBreakTime, rate, negotiable, total, billToMedicare) => {
    setLoading(true);
    const changeSet = {
      startTime,
      endTime,
      unpaidBreakTime: Math.round((unpaidBreakTime / 60) * MILLISECONDS_IN_AN_HOUR),
      rate: Math.round(rate * 100),
      negotiable,
      total: Math.round(total * 100),
      billToMedicare,
    };
    postShiftEdit(shift._id, changeSet, reason)
      .then(() => {
        getShiftsForStore(shift.storeId)
          .then(({ shifts }) => {
            setShifts(shifts);
            const selectedShift = shifts.find(({ _id }) => _id === shift._id);
            select(selectedShift);
            setLoading(false);
            close();
          })
          .catch(() => {
            setLoading(false);
            setErrorText('Shift updated, but shifts could not refresh. Please reload the page.');
          });
      })
      .catch((err) => {
        setLoading(false);
        setErrorText(err.message || 'An error occurred! Please report this to the administrators.');
      });
  };
  return (
    <>
      <button className={`${classNames.join(' ')} very-dark ${disabled ? 'disabled' : ''}`} onClick={() => setOpen(true)} disabled={disabled}>
        {editText}
      </button>
      { open && <EditShiftConfirmation close={close} confirm={confirm} setReason={setReason} reason={reason} shift={shift} errorText={errorText} /> }
    </>
  );
};

export const EditShiftConfirmationModal = EditShiftConfirmation;

export { EDIT_OPTIONS };
